import type { PlatformType } from "./utils-v2";

export type SlipLadder = Readonly<Record<number, number>>;

export type SlipTypeBreakeven = {
  id: string;
  label: string;
  breakevenPct: number;
  ladder?: SlipLadder;
  leagues?: Readonly<Record<string, SlipLadder>>;
};

type PickLadders = Readonly<Record<number, SlipLadder>>;

const PICK6_EIGHT: PickLadders = { 2: { 2: 3 }, 3: { 3: 6 }, 4: { 4: 10 }, 5: { 5: 12, 4: 1 }, 6: { 6: 25, 5: 1.5 }, 7: { 7: 40, 6: 2 }, 8: { 8: 80, 7: 3, 6: 1 } };
const PICK6_SIX: PickLadders = { 2: { 2: 3 }, 3: { 3: 5.5 }, 4: { 4: 10 }, 5: { 5: 12, 4: 1 }, 6: { 6: 25, 5: 1.5 } };
const PICK6_LOW: PickLadders = { 2: { 2: 2.5 }, 3: { 3: 4 }, 4: { 4: 6 } };

export const PICK6_LEAGUES: Readonly<Record<string, PickLadders>> = {
  NFL: PICK6_EIGHT,
  MLB: PICK6_EIGHT,
  NBA: PICK6_EIGHT,
  NHL: { 2: { 2: 3 }, 3: { 3: 6 }, 4: { 4: 10 }, 5: { 5: 12, 4: 1 }, 6: { 6: 25, 5: 1.5 } },
  CFB: PICK6_SIX,
  WNBA: PICK6_SIX,
  SOCCER: { 2: { 2: 3 }, 3: { 3: 5.5 }, 4: { 4: 10 } },
  MMA: { 2: { 2: 2.7 }, 3: { 3: 5 }, 4: { 4: 8 }, 5: { 5: 10, 4: 1 }, 6: { 6: 18, 5: 1.5 } },
  GOLF: { ...PICK6_LOW, 5: { 5: 8, 4: 1 } },
  VALORANT: PICK6_LOW,
  LOL: { 2: { 2: 2.7 }, 3: { 3: 4 }, 4: { 4: 7 }, 5: { 5: 10, 4: 1 }, 6: { 6: 15, 5: 1.5 } },
};

function pick6SlipTypes(): SlipTypeBreakeven[] {
  return Object.entries(PICK6_EIGHT).map(([picks, ladder]) => {
    const insured = Object.keys(ladder).length > 1;
    const leagues = Object.fromEntries(Object.entries(PICK6_LEAGUES).flatMap(([league, table]) => (table[Number(picks)] ? [[league, table[Number(picks)]]] : [])));
    return {
      id: `${picks}-${insured ? "insured" : "non-insured"}`,
      label: `${picks} Pick ${insured ? "Insured" : "Non-Insured"}`,
      breakevenPct: Math.round(breakevenPctFromLadder(ladder) * 100) / 100,
      ladder,
      leagues,
    };
  });
}

const RAW_SLIP_BREAKEVENS: Partial<Record<PlatformType, SlipTypeBreakeven[]>> = {
  prizepicks: [
    { id: "2-power", label: "2 Pick Power", breakevenPct: 57.6 },
    { id: "3-flex", label: "3 Pick Flex", breakevenPct: 57.8, ladder: { 3: 3, 2: 1 } },
    { id: "3-power", label: "3 Pick Power", breakevenPct: 55 },
    { id: "4-flex", label: "4 Pick Flex", breakevenPct: 55, ladder: { 4: 6, 3: 1.5 } },
    { id: "4-power", label: "4 Pick Power", breakevenPct: 56.1 },
    { id: "5-flex", label: "5 Pick Flex", breakevenPct: 54.3, ladder: { 5: 10, 4: 2, 3: 0.4 } },
    { id: "5-power", label: "5 Pick Power", breakevenPct: 55 },
    { id: "6-flex", label: "6 Pick Flex", breakevenPct: 54.3, ladder: { 6: 25, 5: 2, 4: 0.4 } },
    { id: "6-power", label: "6 Pick Power", breakevenPct: 54.8 },
  ],
  underdog: [
    { id: "2-non-insured", label: "2 Pick Non-Insured", breakevenPct: 53.45 },
    { id: "3-insured", label: "3 Pick Insured", breakevenPct: 55.39, ladder: { 3: 3.25, 2: 1.09 } },
    { id: "3-non-insured", label: "3 Pick Non-Insured", breakevenPct: 53.58 },
    { id: "4-insured", label: "4 Pick Insured", breakevenPct: 53.7, ladder: { 4: 7.2, 3: 1.4 } },
    { id: "4-non-insured", label: "4 Pick Non-Insured", breakevenPct: 53.73 },
    { id: "5-insured", label: "5 Pick Insured", breakevenPct: 54.75, ladder: { 5: 10, 4: 2.5 } },
    { id: "5-non-insured", label: "5 Pick Non-Insured", breakevenPct: 54.93, ladder: { 5: 20 } },
    { id: "6-insured", label: "6 Pick Insured", breakevenPct: 53.82, ladder: { 6: 25, 5: 2.6, 4: 0.25 } },
    { id: "7-insured", label: "7 Pick Insured", breakevenPct: 55.42, ladder: { 7: 40, 6: 2.75, 5: 0.5 } },
    { id: "8-insured", label: "8 Pick Insured", breakevenPct: 55.07, ladder: { 8: 80, 7: 3, 6: 1 } },
  ],
  betr: [
    { id: "2-non-insured", label: "2 Pick Non-Insured", breakevenPct: 57.8, ladder: { 2: 3 } },
    { id: "3-insured", label: "3 Pick Insured", breakevenPct: 57.7 },
    { id: "3-non-insured", label: "3 Pick Non-Insured", breakevenPct: 55, ladder: { 3: 6 } },
    { id: "4-insured", label: "4 Pick Insured", breakevenPct: 55 },
    { id: "4-non-insured", label: "4 Pick Non-Insured", breakevenPct: 56.3, ladder: { 4: 10 } },
    { id: "5-insured", label: "5 Pick Insured", breakevenPct: 54.3, ladder: { 5: 10, 4: 2, 3: 0.4 } },
    { id: "5-non-insured", label: "5 Pick Non-Insured", breakevenPct: 55, ladder: { 5: 20 } },
    { id: "6-insured", label: "6 Pick Insured", breakevenPct: 54.6 },
    { id: "6-non-insured", label: "6 Pick Non-Insured", breakevenPct: 55.4, ladder: { 6: 35 } },
    { id: "7-insured", label: "7 Pick Insured", breakevenPct: 55.3 },
    { id: "7-non-insured", label: "7 Pick Non-Insured", breakevenPct: 57.3, ladder: { 7: 50 } },
    { id: "8-insured", label: "8 Pick Insured", breakevenPct: 54.4 },
    { id: "8-non-insured", label: "8 Pick Non-Insured", breakevenPct: 56.3, ladder: { 8: 100 } },
  ],
  dabble: [
    { id: "2-non-insured", label: "2 Pick Non-Insured", breakevenPct: 57.6 },
    { id: "3-insured", label: "3 Pick Insured", breakevenPct: 59 },
    { id: "3-non-insured", label: "3 Pick Non-Insured", breakevenPct: 55 },
    { id: "4-insured", label: "4 Pick Insured", breakevenPct: 56.9 },
    { id: "4-non-insured", label: "4 Pick Non-Insured", breakevenPct: 56.1 },
    { id: "5-insured", label: "5 Pick Insured", breakevenPct: 55.8 },
    { id: "5-non-insured", label: "5 Pick Non-Insured", breakevenPct: 54.8 },
    { id: "6-insured", label: "6 Pick Insured", breakevenPct: 55 },
    { id: "6-non-insured", label: "6 Pick Non-Insured", breakevenPct: 55.4 },
    { id: "7-insured", label: "7 Pick Insured", breakevenPct: 56.3 },
    { id: "7-non-insured", label: "7 Pick Non-Insured", breakevenPct: 55.8 },
  ],
  dkpick6: pick6SlipTypes(),
};

const ALL_OR_NOTHING_SLIP = /-(power|non-insured)$/;

function binomial(n: number, k: number): number {
  let result = 1;
  for (let i = 1; i <= k; i++) result = (result * (n - k + i)) / i;
  return result;
}

export function breakevenPctFromLadder(ladder: SlipLadder): number {
  const tiers = Object.entries(ladder).map(([correct, payout]) => [Number(correct), payout] as const);
  const legs = Math.max(...tiers.map(([correct]) => correct));
  const expectedReturn = (p: number) =>
    tiers.reduce((sum, [correct, payout]) => sum + binomial(legs, correct) * p ** correct * (1 - p) ** (legs - correct) * payout, 0);
  let low = 0;
  let high = 1;
  for (let step = 0; step < 64; step++) {
    const mid = (low + high) / 2;
    if (expectedReturn(mid) < 1) low = mid;
    else high = mid;
  }
  return ((low + high) / 2) * 100;
}

const PICK6_PLATFORMS: ReadonlySet<PlatformType> = new Set(["dkpick6"]);

export function promoBlocksBoost(platform: PlatformType): boolean {
  return PICK6_PLATFORMS.has(platform);
}

export function boostLadder(platform: PlatformType, ladder: SlipLadder, boostPct: number): SlipLadder {
  const factor = 1 + boostPct / 100;
  const boost = PICK6_PLATFORMS.has(platform)
    ? (payout: number) => (payout > 1 ? payout * factor : payout)
    : (payout: number) => payout * factor;
  return Object.fromEntries(Object.entries(ladder).map(([correct, payout]) => [correct, boost(payout)]));
}

export function boostedBreakevenPct(platform: PlatformType, slip: SlipTypeBreakeven, boostPct: number): number {
  if (!slip.ladder || !(boostPct > 0)) return slip.breakevenPct;
  const scale = breakevenPctFromLadder(boostLadder(platform, slip.ladder, boostPct)) / breakevenPctFromLadder(slip.ladder);
  return Math.round(slip.breakevenPct * scale * 100) / 100;
}

export function applyBoostToBreakevens(platform: PlatformType, breakevens: readonly SlipTypeBreakeven[], boostPct: number): SlipTypeBreakeven[] {
  return breakevens.map((slip) => ({ ...slip, breakevenPct: boostedBreakevenPct(platform, slip, boostPct) }));
}

function snapPayout(multiple: number): number {
  const quarter = Math.round(multiple * 4) / 4;
  return Math.abs(quarter - multiple) / multiple < 0.01 ? quarter : Math.round(multiple * 100) / 100;
}

function withLadders(defs: SlipTypeBreakeven[]): SlipTypeBreakeven[] {
  return defs.map((slip) => {
    if (slip.ladder || !ALL_OR_NOTHING_SLIP.test(slip.id)) return slip;
    const legs = slipTypeLegCount(slip.id);
    return legs > 0 ? { ...slip, ladder: { [legs]: snapPayout((100 / slip.breakevenPct) ** legs) } } : slip;
  });
}

export function slipTypeForLeague(slip: SlipTypeBreakeven, league: string | null | undefined): SlipTypeBreakeven | null {
  if (!league || slip.leagues === undefined) return slip;
  const ladder = slip.leagues[league.toUpperCase()];
  return ladder === undefined ? null : { ...slip, ladder, breakevenPct: Math.round(breakevenPctFromLadder(ladder) * 100) / 100 };
}

export const SLIP_BREAKEVENS_BY_PLATFORM: Partial<Record<PlatformType, SlipTypeBreakeven[]>> = Object.fromEntries(
  Object.entries(RAW_SLIP_BREAKEVENS).map(([platform, defs]) => [platform, withLadders(defs ?? [])]),
);

export function isBoostableSlipPlatform(platform: PlatformType): boolean {
  return (SLIP_BREAKEVENS_BY_PLATFORM[platform] ?? []).some((slip) => slip.ladder !== undefined);
}

export function getSlipBreakevens(platform: PlatformType): SlipTypeBreakeven[] | null {
  return SLIP_BREAKEVENS_BY_PLATFORM[platform] ?? null;
}

export function normalizeOddsToHitPercent(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const numeric = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numeric)) return null;
  return numeric <= 1 ? numeric * 100 : numeric;
}

export function adjustBreakevensForOdds(
  platform: PlatformType,
  breakevens: SlipTypeBreakeven[],
  americanOdds: number | null | undefined,
  baseDecimal: number | null,
): SlipTypeBreakeven[] | null {
  const pick6 = PICK6_PLATFORMS.has(platform);
  if (platform !== "underdog" && !pick6) return null;
  if (americanOdds == null || !Number.isFinite(americanOdds) || americanOdds === 0) return null;
  if (baseDecimal === null) return [];
  if (pick6) return null;

  const decimal = americanOdds > 0
    ? americanOdds / 100 + 1
    : 100 / Math.abs(americanOdds) + 1;

  if (Math.abs(decimal - baseDecimal) < 0.01) return null;

  const factor = baseDecimal / decimal;

  return breakevens.map((slip) => ({
    ...slip,
    breakevenPct: Math.round(slip.breakevenPct * factor * 100) / 100,
  }));
}


export function isExplicitSlipTypePlatform(platform: PlatformType): boolean {
  return (SLIP_BREAKEVENS_BY_PLATFORM[platform]?.length ?? 0) > 0;
}

export function getSelectableSlipTypes(platform: PlatformType): SlipTypeBreakeven[] {
  if (!isExplicitSlipTypePlatform(platform)) return [];
  return SLIP_BREAKEVENS_BY_PLATFORM[platform] ?? [];
}

export function slipTypeLegCount(slipTypeId: string): number {
  const legs = Number.parseInt(slipTypeId, 10);
  return Number.isFinite(legs) ? legs : 0;
}

export function normalizeSlipTypeId(
  platform: PlatformType,
  raw: string | null | undefined,
): string {
  if (!raw || !raw.trim()) return "";
  const trimmed = raw.trim();
  const defs = SLIP_BREAKEVENS_BY_PLATFORM[platform];
  if (!defs) return "";
  const byId = defs.find((d) => d.id === trimmed);
  if (byId) return byId.id;
  const lower = trimmed.toLowerCase();
  const byLabel = defs.find((d) => d.label.toLowerCase() === lower);
  if (byLabel) return byLabel.id;
  return "";
}

export function getSlipTypeLabel(
  platform: PlatformType,
  slipTypeId: string | null | undefined,
): string | null {
  if (!slipTypeId) return null;
  const defs = SLIP_BREAKEVENS_BY_PLATFORM[platform];
  if (!defs) return null;
  const entry = defs.find((d) => d.id === slipTypeId);
  return entry?.label ?? null;
}

export type EffectiveSlipContext = {
  effectiveSlipTypeId: string | null;
  effectiveSlipTypeLabel: string | null;
  effectiveBreakevenPct: number | null;
  edgePct: number | null;
  fairWinPct: number | null;
  usedImplicitBestSlip: boolean;
};

export function resolveEffectiveSlipContext(args: {
  platform: PlatformType;
  rawSlipTypeId?: string | null;
  americanOdds?: number | null;
  fairWinPct?: number | null;
  boostPct?: number;
  standardDecimal?: number | null;
  league?: string | null;
}): EffectiveSlipContext {
  const { platform, rawSlipTypeId, americanOdds, fairWinPct, boostPct = 0, standardDecimal, league } = args;
  const empty: EffectiveSlipContext = {
    effectiveSlipTypeId: null,
    effectiveSlipTypeLabel: null,
    effectiveBreakevenPct: null,
    edgePct: null,
    fairWinPct: fairWinPct ?? null,
    usedImplicitBestSlip: false,
  };

  const platformDefs = SLIP_BREAKEVENS_BY_PLATFORM[platform];
  if (!platformDefs || platformDefs.length === 0) return empty;
  const baseDefs = platformDefs.flatMap((slip) => slipTypeForLeague(slip, league) ?? []);
  if (baseDefs.length === 0) return empty;

  const boosted = applyBoostToBreakevens(platform, baseDefs, boostPct);
  const adjusted = adjustBreakevensForOdds(platform, boosted, americanOdds, standardDecimal ?? null);
  const breakevens = adjusted ?? boosted;

  let selected: SlipTypeBreakeven | undefined;
  let usedImplicit = false;

  if (rawSlipTypeId) {
    selected = breakevens.find((b) => b.id === rawSlipTypeId);
  }

  if (!selected) {
    usedImplicit = true;
    selected = breakevens.reduce((best, current) =>
      current.breakevenPct < best.breakevenPct ? current : best
    , breakevens[0]);
  }

  if (!selected) return empty;

  const breakevenPct = Math.round(selected.breakevenPct * 100) / 100;
  const edgePct = fairWinPct != null
    ? Math.round((fairWinPct - breakevenPct) * 100) / 100
    : null;

  return {
    effectiveSlipTypeId: selected.id,
    effectiveSlipTypeLabel: selected.label,
    effectiveBreakevenPct: breakevenPct,
    edgePct,
    fairWinPct: fairWinPct ?? null,
    usedImplicitBestSlip: usedImplicit,
  };
}

export function getPlatformDisplayName(platform: PlatformType): string {
  const names: Partial<Record<PlatformType, string>> = {
    prizepicks: "PrizePicks",
    underdog: "Underdog",
    betr: "Betr",
    dabble: "Dabble",
    dkpick6: "DK Pick6",
  };
  return names[platform] ?? platform;
}
